// src/v3/symbol/domain/schemas/filter-clause.ts

/**
 * Top-level (non-JSONB) columns that the backend can filter and sort on.
 * These map directly to table columns (no JSONB path traversal needed).
 */
export const TOP_LEVEL_COLUMNS = [
  "symbol",
  "name",
  "exchange",
  "price",
  "volume",
  "marketCap",
  "avgVolume",
  "sharesOutstanding",
  "autoStatus",
  "manualStatus",
  "type",
  "country",
  "currency",
  "timestamp.createdAt",
  "timestamp.updatedAt",
  // Frontend alias — the SymbolPage column id is "updatedAt" (without the
  // "timestamp." prefix). Listed here so partitionFilters sends it server-side.
  "updatedAt",
] as const;

/**
 * JSONB column prefixes that the backend can filter and sort on generically.
 * Any dot-path starting with one of these prefixes (and having ≥2 segments)
 * is treated as server-side — the drizzle adapter extracts the value via
 * JSONB path traversal (dotPathToSql).
 *
 * This is prefix-based (not an exhaustive list) so that new financials.*
 * relations.* fields automatically become server-side without updating this
 * file. The backend adapter validates segment names and handles safe casting.
 */
export const JSONB_COLUMN_PREFIXES = ["financials", "apiData", "relations"] as const;

/**
 * Curated JSONB fields with dedicated DB indexes (for performance-critical
 * queries). These are a subset of all server-side JSONB fields — the index
 * hints are created in the table definition.
 */
export const CURATED_JSONB_FIELDS = [
  "financials.flags.isInterestingPrice",
  "financials.flags.isNoInterestStatus",
  "financials.flags.isExpensiveStatus",
  "financials.flags.isVeryExpensiveStatus",
  "financials.flags.isInterestingPriceBroadStatus",
  "relations.industry.name",
  "relations.country.name",
] as const;

export const isTopLevelColumn = (column: string): boolean =>
  TOP_LEVEL_COLUMNS.some((topLevelColumn) => topLevelColumn === column);

/**
 * A column is server-side JSONB if it starts with a known JSONB column prefix
 * and has at least 2 non-empty segments (e.g. "financials.prices.buy" — not
 * just "financials" or "financials."). Empty segments are rejected to prevent
 * dotPathToSql from throwing on malformed paths (e.g. crafted URLs).
 */
export const isCuratedJsonbField = (column: string): boolean => {
  const parts = column.split(".");
  const prefix = parts[0];
  if (!prefix) return false;
  if (!JSONB_COLUMN_PREFIXES.some((knownPrefix) => knownPrefix === prefix)) {
    return false;
  }
  if (parts.length < 2) return false;
  // Reject paths with empty segments (e.g. "financials." or "financials..flags")
  for (let i = 1; i < parts.length; i++) {
    if (!parts[i]) return false;
  }
  return true;
};

export const isServerSideField = (column: string): boolean =>
  isTopLevelColumn(column) || isCuratedJsonbField(column);

export const isClientSideField = (column: string): boolean =>
  !isServerSideField(column);

export function partitionFilters<T extends { column: string }>(
  filters: T[],
): { serverSide: T[]; clientSide: T[] } {
  const serverSide: T[] = [];
  const clientSide: T[] = [];
  for (const f of filters) {
    if (isServerSideField(f.column)) serverSide.push(f);
    else clientSide.push(f);
  }
  return { serverSide, clientSide };
}
